import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for managing dashboard auto-refresh functionality
 * @param {Function} refreshCallback - Function to call when refreshing
 * @param {number} interval - Refresh interval in milliseconds (default: 30000 = 30 seconds)
 * @returns {Object} - { refreshing, lastRefreshTime, handleManualRefresh }
 */
export const useDashboardRefresh = (refreshCallback, interval = 30000) => {
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const refreshIntervalRef = useRef(null);

  // Auto-refresh on interval
  useEffect(() => {
    if (interval > 0) {
      refreshIntervalRef.current = setInterval(async () => {
        await performRefresh(true);
      }, interval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [interval, refreshCallback]);

  // Refresh when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        performRefresh(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshCallback]);

  const performRefresh = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setRefreshing(true);
      }
      await refreshCallback();
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('Dashboard refresh failed:', error);
    } finally {
      if (!silent) {
        setRefreshing(false);
      }
    }
  }, [refreshCallback]);

  const handleManualRefresh = useCallback(async () => {
    await performRefresh(false);
  }, [performRefresh]);

  return {
    refreshing,
    lastRefreshTime,
    handleManualRefresh,
    performRefresh
  };
};
